import { approxEqual, EPSILON } from './epsilon';

export const TAU = Math.PI * 2;
export const HALF_PI = Math.PI / 2;
export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

export function toRadians(degrees: number): number {
  return degrees * DEG_TO_RAD;
}

export function toDegrees(radians: number): number {
  return radians * RAD_TO_DEG;
}

/**
 * Normalises to `[0, 2π)`.
 *
 * Uses a modulo rather than a `while` loop: angles accumulated over a long drag can be
 * arbitrarily large, and a loop would stall.
 */
export function normalize(radians: number): number {
  const r = radians % TAU;
  return r < 0 ? r + TAU : r;
}

/** Normalises to `(-π, π]` — the range `Math.atan2` produces. */
export function normalizeSigned(radians: number): number {
  const r = normalize(radians);
  return r > Math.PI ? r - TAU : r;
}

/**
 * Shortest signed rotation from `from` to `to`, in `(-π, π]`.
 *
 * Positive is counter-clockwise. This is what a rotation gesture needs: rotating from
 * 350° to 10° must yield +20°, not −340°.
 */
export function difference(from: number, to: number): number {
  return normalizeSigned(to - from);
}

/** Unsigned separation between two directions, in `[0, π]`. */
export function absoluteDifference(from: number, to: number): number {
  return Math.abs(difference(from, to));
}

/**
 * Angle between two *undirected* lines, in `[0, π/2]`.
 *
 * A line has no orientation, so directions `θ` and `θ + π` describe the same line.
 * This is the measure a student means by "the angle between these two lines", as
 * opposed to the angle between two rays.
 */
export function betweenLines(a: number, b: number): number {
  const d = absoluteDifference(a, b);
  return d > HALF_PI ? Math.PI - d : d;
}

/** True when the two angles denote the same direction, modulo full turns. */
export function equals(a: number, b: number, epsilon: number = EPSILON): boolean {
  return approxEqual(Math.abs(difference(a, b)), 0, epsilon);
}

/** True when the two angles denote the same *undirected* line. */
export function equalsAsLine(a: number, b: number, epsilon: number = EPSILON): boolean {
  return approxEqual(betweenLines(a, b), 0, epsilon);
}

/**
 * True when `angle` lies on the arc travelling from `start` to `end`.
 *
 * `counterclockwise` selects which of the two arcs between the endpoints is meant —
 * without it, "is this angle on the arc" is ambiguous, and picking the wrong arc is a
 * common source of wrong hit-testing on arcs.
 *
 * Semantics match `CanvasRenderingContext2D.arc`, which matters because the same
 * start/end/direction triple drives both hit-testing and painting:
 *
 * - The sweep is measured from the **raw** arguments, before normalisation. A sweep of
 *   a full turn or more covers every angle, so `(0, 2π)` is a complete circle. This
 *   only works if the raw values survive: `normalize(2π)` is `0`, which would erase the
 *   distinction between a full circle and a degenerate arc.
 * - Otherwise the sweep wraps into `[0, 2π)`, so `(350°, 10°)` is a 20° arc across the
 *   seam rather than a −340° one.
 * - Identical endpoints are a degenerate, single-point arc — only `start` itself is on
 *   it. A full circle is a `Circle`, not an `Arc`.
 */
export function isOnArc(
  angle: number,
  start: number,
  end: number,
  counterclockwise = false,
  epsilon: number = EPSILON
): boolean {
  const rawSweep = counterclockwise ? start - end : end - start;
  if (rawSweep >= TAU - epsilon) return true;

  const sweep = normalize(rawSweep);
  const offset = counterclockwise ? normalize(start - angle) : normalize(angle - start);

  // The second term catches an angle a hair *before* `start`, whose offset wraps to
  // just under 2π rather than sitting just above 0.
  return offset <= sweep + epsilon || offset >= TAU - epsilon;
}

/** Snaps to the nearest multiple of `step` — the behaviour behind shift-constrained rotation. */
export function snap(radians: number, step: number): number {
  if (step <= 0) return radians;
  return Math.round(radians / step) * step;
}
